import React, { useEffect, useRef, useState } from "react";

const YouTubePlayer = ({ 
  videoUrl, 
  onVideoEnd, 
  currentItem, 
  sectionId, 
  moduleId 
}) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const hasEndedRef = useRef(false);
  const watchedPercentageRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Reset tracking when video changes
    hasEndedRef.current = false;
    watchedPercentageRef.current = 0;

    // Load YouTube IFrame API
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Initialize player when API is ready
    const initializePlayer = () => {
      // Extract video ID from embed URL
      const videoId = videoUrl?.split("/embed/")[1]?.split("?")[0];

      if (videoId && iframeRef.current) {
        // Destroy existing player if any
        if (playerRef.current) {
          playerRef.current.destroy();
        }

        playerRef.current = new window.YT.Player(iframeRef.current, {
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            fs: 1,
            enablejsapi: 1,
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
          },
        });
      }
    };

    window.onYouTubeIframeAPIReady = () => {
      initializePlayer();
    };

    if (window.YT && window.YT.Player) {
      initializePlayer();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.error("Error destroying player:", error);
        }
      }
    };
  }, [videoUrl, currentItem?.id]);

  const onPlayerReady = (event) => {
    console.log("YouTube Player Ready");
    
    // Start tracking progress
    intervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getDuration) {
        try {
          const currentTime = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          
          if (duration > 0) {
            const percentage = (currentTime / duration) * 100;
            watchedPercentageRef.current = Math.max(
              watchedPercentageRef.current,
              percentage
            );
            
            // Log progress (optional)
            // console.log(`Watched: ${watchedPercentageRef.current.toFixed(2)}%`);
          }
        } catch (error) {
          console.error("Error tracking progress:", error);
        }
      }
    }, 1000);
  };

  const onPlayerStateChange = (event) => {
    const state = event.data;
    
    // YT.PlayerState values:
    // -1 (unstarted)
    // 0 (ended)
    // 1 (playing)
    // 2 (paused)
    // 3 (buffering)
    // 5 (video cued)

    switch (state) {
      case window.YT.PlayerState.ENDED:
        handleVideoEnd();
        break;
      case window.YT.PlayerState.PLAYING:
        // Reset hasEnded when video starts playing again
        hasEndedRef.current = false;
        console.log("YouTube video playing");
        break;
      case window.YT.PlayerState.PAUSED:
        console.log("YouTube video paused");
        break;
      default:
        break;
    }
  };

  const handleVideoEnd = () => {
    if (hasEndedRef.current) {
      return; // Prevent multiple calls
    }

    hasEndedRef.current = true;
    console.log("YouTube Video ended, triggering completion");
    console.log(`Video watched: ${watchedPercentageRef.current.toFixed(2)}%`);

    // Clear the tracking interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Trigger the completion callback with all required parameters
    if (onVideoEnd) {
      onVideoEnd(currentItem, sectionId, moduleId);
    }
  };

  // Manual completion trigger (can be called externally if needed)
  const triggerCompletion = () => {
    if (watchedPercentageRef.current >= 90) {
      handleVideoEnd();
    }
  };

  return (
    <div className="absolute top-0 left-0 w-full h-full bg-black">
      <div 
        ref={iframeRef} 
        className="w-full h-full"
        style={{ border: 'none' }}
      ></div>
    </div>
  );
};

export default YouTubePlayer;